import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GoBack from "../components/GoBack";
import ConfirmModal from "../components/ConfirmModal";
import IncomeEntryTable from "../components/application/IncomeEntryTable";
import IncomeEntryForm from "../components/application/IncomeEntryForm";
import {
  getIncomeEntries,
  createIncomeEntry,
  updateIncomeEntry,
  deleteIncomeEntry,
  ApplicationIncomeDto,
  ApplicationIncomeInput,
} from "../api/services";
import { DocumentCard } from "../components/common/DocumentCard";

const IncomeSources: React.FC = () => {
  const { t } = useTranslation();
  const { id: applicationId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [entries, setEntries] = useState<ApplicationIncomeDto[]>([]);
  const [loadingIncome, setLoadingIncome] = useState(true);

  const [showFormModal, setShowFormModal] = useState(false);

  const [editing, setEditing] = useState<ApplicationIncomeDto | null>(null);

  const [deletingIncome, setDeletingIncome] =
    useState<ApplicationIncomeDto | null>(null);

  const fetchEntries = () => {
    if (!applicationId) return;
    setLoadingIncome(true);
    getIncomeEntries(applicationId)
      .then((res) => setEntries(res.data))
      .finally(() => setLoadingIncome(false));
  };

  useEffect(fetchEntries, [applicationId]);

  const handleAddClick = () => {
    setEditing(null);
    setShowFormModal(true);
  };

  const handleEditClick = (item: ApplicationIncomeDto) => {
    setEditing(item);
    setShowFormModal(true);
  };

  const handleDeleteClick = (item: ApplicationIncomeDto) => {
    setDeletingIncome(item);
  };
  const confirmDelete = () => {
    if (!applicationId || !deletingIncome) return;
    deleteIncomeEntry(applicationId, deletingIncome.id).then(() => {
      setDeletingIncome(null);
      fetchEntries();
    });
  };
  const cancelDelete = () => setDeletingIncome(null);

  // Called when the modal form is submitted
  const handleSave = (data: ApplicationIncomeInput, id?: number) => {
    if (!applicationId) return;
    const fn = id
      ? updateIncomeEntry(applicationId, id, data)
      : createIncomeEntry(applicationId, data);

    fn.then(() => {
      setShowFormModal(false);
      fetchEntries();
    });
  };

  if (loadingIncome) {
    return (
      <div className="mt-6">
        <GoBack />
        <p>{t("employmentVerification.loading", "Loading...")}</p>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <GoBack />
      <h2 className="mb-4">{t("employmentVerification.title")}</h2>

      {entries.length > 0 && (
        <IncomeEntryTable
          entries={entries}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
        />
      )}

      <div className="mb-6">
        <button className="rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light" onClick={handleAddClick}>
          {t("employmentVerification.buttons.add")}
        </button>
      </div>

      {showFormModal && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        >
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <h3 className="font-semibold text-primary">
                {editing
                  ? t(
                      "employmentVerification.editTitle",
                      "Edit Income Source"
                    )
                  : t("employmentVerification.buttons.add")}
              </h3>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
                onClick={() => setShowFormModal(false)}
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-6">
              <IncomeEntryForm
                initial={
                  editing
                    ? {
                        type: editing.type,
                        description: editing.description,
                        company: editing.company,
                        address: editing.address,
                        phone: editing.phone,
                        jobTitle: editing.jobTitle,
                        monthlyNetIncome: editing.monthlyNetIncome,
                        startDate: editing.startDate,
                        endDate: editing.endDate || "",
                        current: editing.current,
                        payFrequency: editing.payFrequency,
                      }
                    : undefined
                }
                onSave={(data) => handleSave(data, editing?.id)}
                onCancel={() => setShowFormModal(false)}
              />
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={!!deletingIncome}
        message={t("employmentVerification.confirmDelete")}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />

      <hr className="my-8" />

      <DocumentCard
        applicationId={applicationId!}
        documentType="paystub"
        titleKey="employmentVerification.paystubs.title"
        inputLabelKey="employmentVerification.paystubs.inputLabel"
        colFileNameKey="employmentVerification.paystubs.colFileName"
        colActionsKey="employmentVerification.paystubs.colActions"
        deleteLabelKey="employmentVerification.paystubs.delete"
        confirmDeleteKey="employmentVerification.paystubs.confirmDelete"
        errorFetchingKey="employmentVerification.paystubs.errorFetching"
        errorDeletingKey="employmentVerification.paystubs.errorDeleting"
        errorUploadingKey="employmentVerification.paystubs.errorUploading"
      />
    </div>
  );
};

export default IncomeSources;
